import { useContext } from 'react';

import { RANGE_FILTER_SECTIONS } from '@/constants/inventory';
import { NewInventoryFiltersContext } from '@/contexts/NewInventoryFiltersContext';
import { hasFilters, isFilterSection, rangeFormatters } from '@/tools/inventory';
import { SortBy } from '@/types/inventory';

interface UseFilterListOptions {
  includeSort?: boolean;
}

export const useFiltersList = ({ includeSort = false }: UseFilterListOptions = {}) => {
  const { filters, toggleFilter, setRangeFilter, query, setQuery, sortBy, setSortBy, tagFilters, toggleTagFilter } =
    useContext(NewInventoryFiltersContext);

  const filtersList: {
    label: string;
    key: string;
    onRemove: () => void;
  }[] = [];

  if (query) {
    filtersList.push({
      label: query.toUpperCase(),
      key: 'query',
      onRemove: () => setQuery(''),
    });
  }

  if (includeSort && sortBy !== SortBy.RECOMMENDED) {
    filtersList.push({
      label: sortBy,
      key: 'sortBy',
      onRemove: () => setSortBy(SortBy.RECOMMENDED),
    });
  }

  for (const [section, filterValues] of Object.entries(filters)) {
    if (!isFilterSection(section)) continue;

    if (RANGE_FILTER_SECTIONS.has(section)) {
      if (filterValues instanceof Array || !filterValues) continue;

      const formatter = rangeFormatters[section];

      const labelFormatter = formatter?.label || ((value: number) => value.toString());

      filtersList.push({
        label: `${labelFormatter(filterValues.min)} - ${labelFormatter(filterValues.max)}`,
        key: section,
        onRemove: () => setRangeFilter(section, null),
      });
      continue;
    }

    if (filterValues instanceof Array) {
      for (const filter of filterValues) {
        filtersList.push({
          label: filter,
          key: section + filter,
          onRemove: () => toggleFilter(section, filter),
        });
      }
    }

    const tagValue = tagFilters[section] || [];
    for (const tag of tagValue) {
      filtersList.push({
        label: tag,
        key: section + tag,
        onRemove: () => toggleTagFilter(section, tag),
      });
    }
  }

  for (const [section, tagValue] of Object.entries(tagFilters)) {
    if (isFilterSection(section) && hasFilters(filters[section])) continue;

    for (const tag of tagValue) {
      filtersList.push({
        label: tag,
        key: section + tag,
        onRemove: () => toggleTagFilter(section, tag),
      });
    }
  }

  return { filtersList };
};
